<template>
  <div
    v-if="hasAnyActiveJobSalaryPosition"
    class="i-median-salaries"
  >
    <iBaseHeader
      highlight-text=""
      text="Average Salary"
      align="start"
    />

    <div class="focus">
      <div>
        <span class="year">${{ highlightedSalaryAnnually.toLocaleString() }}</span> yearly
      </div>
      <div>
        <span class="hour">${{ highlightedSalaryHourly.toLocaleString() }}</span> hourly
      </div>
    </div>

    <div class="chart">
      <div
        v-for="(salary, index) in salaryData"
        :key="index"
        class="line"
        :class="{ active: data.activeSalaryIndex === index }"
        :style="{ '--color': salary.color }"
      >
        <div class="top">
          <div class="pointer"></div>
          <div
            class="bar"
            :style="{ '--height': salary.height }"
            @mouseover="data.activeSalaryIndex = index"
          ></div>
        </div>
        <div
          v-if="!!salary.stat"
          class="bottom"
        >
          <div>${{ salary.salary.toLocaleString() }}</div>
          <div :style="{ color: black500 }">{{ salary.stat }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue"
import { useReportStore } from "@/stores/report"
import { azureRadiance500, black500, riptide300, sorbus300, sorbus600 } from "@/styles/colors"

import iBaseHeader from "@/components/base/BaseHeader.vue"

// STORES
const reportStore = useReportStore()

// DATA
const data = reactive({
  activeSalaryIndex: 3
})

// COMPUTED
const activeJobSalaryPositions = computed(() => reportStore.getActiveJobSalaryPositions)
const salaryData = computed(() => {
  const entry = parseInt(activeJobSalaryPositions.value.entry?.replace("$", "").replace(",", "") || "0")
  const mid = parseInt(activeJobSalaryPositions.value.mid?.replace("$", "").replace(",", "") || "0")
  const senior = parseInt(activeJobSalaryPositions.value.senior?.replace("$", "").replace(",", "") || "0")

  const output = [
    { stat: "Entry", salary: entry, height: "0.45", color: sorbus600 },
    { salary: (mid - entry) / 3 + entry, height: "0.8", color: sorbus300 },
    { salary: ((mid - entry) / 3) * 2 + entry, height: "1", color: riptide300 },
    { stat: "Median", salary: mid, height: "1", color: azureRadiance500 },
    { salary: (senior - mid) / 3 + mid, height: "0.8", color: riptide300 },
    { salary: ((senior - mid) / 3) * 2 + mid, height: "0.35", color: sorbus300 },
    { stat: "Senior", salary: senior, height: "0.3", color: sorbus600 }
  ]

  return output
})
const hasAnyActiveJobSalaryPosition = computed(
  () =>
    !!activeJobSalaryPositions.value?.entry ||
    !!activeJobSalaryPositions.value?.mid ||
    !!activeJobSalaryPositions.value?.senior
)
const highlightedSalaryAnnually = computed(
  () => Math.round(salaryData.value[data.activeSalaryIndex].salary / 1000) * 1000
)
const highlightedSalaryHourly = computed(() => Math.round((highlightedSalaryAnnually.value / 2080) * 100) / 100)
</script>

<style lang="scss" scoped>
.i-median-salaries {
  @include flex(column, $justify: start, $align: start, $gap: 20px);

  width: 100%;
  padding: 24px;

  @include border-radius-lg;

  border: 1px solid $zumthor100;
  color: $pickle-bluewood900;
  margin: 25px 0 40px;

  .focus {
    margin-top: 15px;

    @include flex(column, start, start, $gap: 4px);
    @include text-sm;

    font-weight: 500;

    .year {
      @include display-sm;
    }

    .hour {
      @include text-md;
    }
  }

  .chart {
    @include flex(row, center, stretch, $gap: 0);

    width: 100%;

    .line {
      @include grid($cols: 1fr, $rows: auto 40px, $align-items: end, $justify-items: center, $gap: 10px);

      width: calc(100% / 7);
      min-width: 0;

      .top {
        @include flex(column, end, center, $gap: 18px);

        height: 180px;
        width: 100%;

        @media screen and (min-width: $breakpoint-sm) {
          height: 250px;
        }

        .pointer {
          width: 0;
          border-top: 14px solid var(--color);
          border-left: 7px solid $white;
          border-right: 7px solid $white;
          visibility: hidden;
        }

        .bar {
          width: 80%;
          background-color: var(--color);
          height: calc((180px - 32px) * var(--height));
          filter: opacity(60%);
          cursor: pointer;

          @media screen and (min-width: $breakpoint-sm) {
            height: calc((250px - 32px) * var(--height));
          }
        }
      }

      &.active {
        .top {
          .pointer {
            visibility: visible;
          }

          .bar {
            filter: opacity(100%);
          }
        }
      }

      .bottom {
        @include text-xs;

        font-weight: 500;
        text-align: center;
        min-width: 0;

        @media screen and (min-width: $breakpoint-sm) {
          @include text-sm;
        }
      }
    }
  }
}
</style>
